import React, { useCallback, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { gql } from '@apollo/client';
import { useQuery, useMutation } from '@apollo/react-hooks';
import { add, isFuture } from 'date-fns';
import { get } from 'lodash/fp';

import useCurrentUser from '../../Hooks/userCurrentUser';
import useFormParams from '../../Hooks/useFormParams';
import Form, { Button, Title } from '../../Components/Form';
import Input from '../../Components/Form/Input';
import useHeaderComponentsMutation from '../../Hooks/useHeaderComponentsMutation';
import useGraphQLErrorExtractor from '../../Hooks/useGraphQLErrorExtractor';
import useRegistrationOptionPermissionChecker from '../../Hooks/useRegistrationOptionPermissionChecker';
import GenericAlert from '../../Components/GenericAlert';
import BackLink from '../../Components/BackLink';
import Loader from '../../Components/Loader';
import Permissions from '../../Constants/PermissionConstrants';
import { EVENT_TITLE } from '../../Constants/AppConstants';
import JoinEventSlider from './JoinEventSlider';
import { raceChoices } from '../../Constants/RaceConstants';
import SchoolSearch from '../../Components/SchoolSearch';


const UPDATE_EVENT_USER_INFO = gql`
  mutation joinEventUpdateUserInfo($input: JoinEventUpdateUserInfoInput!, $image: Upload) {
    joinEventUpdateUserInfo(input: $input, verificationFile: $image) {
      eventUser {
        id
        grade
        fetchUser {
          emergencyContactName
          emergencyContactNumber
          race
        }
        schoolId
        fetchSchoolUser {
          fetchUserAttachment {
            id
            verificationFileUrl
          }
          fetchSchool {
            id
            name
          }
        }
      }
    }
  }
`;

const FETCH_ORDER_DETAIL = gql`
  query fetchOrderDetail($id:Int!) {
    fetchOrderDetail(id:$id) {
      id
      eventId
      fetchEventsUser {
        id
        roleId
        userId
        schoolId
        grade
        fetchUser {
          emergencyContactName
          emergencyContactNumber
          race
        }
        fetchSchoolUser {
          fetchUserAttachment {
            id
            verificationFileUrl
          }
          fetchSchool {
            id
            name
          }
        }
        fetchRegistrationOption {
          id
          title
          fee
          lateFee
          fetchRole {
            id
            title
            permissions {
              id
              title
              displayName
            }
          }
        }
        fetchEvent {
          id
          title
          merchandise {
            id
            title
            cost
          }
        }
      }
    }
  }
`;

const JoinEventPersonalDetails = () => {
  const {
    currentUser,
  } = useCurrentUser(true);

  // they turn 18 sometime in the future
  const isUnder18 = isFuture(add(new Date(currentUser?.dateOfBirth), { years: 18 }));

  const extractError = useGraphQLErrorExtractor();
  const history = useHistory();

  const [errorMessage, setErrorMessage] = useState('');
  const emergencyContactError = 'Emergency contact number cannot be the same as your phone number';
  const { orderId } = useParams();

  const [selectedSchool, setSelectedSchool] = useState({ id: '', name: '' });
  const [schoolIdFile, setSchoolIdFile] = useState(null);

  const { params, onChange, setParams } = useFormParams({
    eventUserId: '',
    emergencyContactName: '',
    emergencyContactNumber: '',
    race: '',
    grade: '',
    schoolId: '',
  });

  const handleSelectSchool = useCallback((school) => {
    setSelectedSchool(school);
    onChange('schoolId', school.id);
  }, [onChange]);

  const { data, loading: orderLoading } = useQuery(
    FETCH_ORDER_DETAIL,
    {
      variables: {
        id: parseInt(orderId, 10),
      },
      fetchPolicy: 'network-only',
      onCompleted: (d) => {
        const eventUser = d.fetchOrderDetail.fetchEventsUser;
        const user = eventUser.fetchUser;
        const schoolUser = eventUser.fetchSchoolUser;
        if (schoolUser) {
          const school = schoolUser.fetchSchool;
          setSelectedSchool({ id: parseInt(school.id, 10), name: school.name });
        }

        setParams({
          eventUserId: parseInt(eventUser.id, 10),
          emergencyContactName: user.emergencyContactName,
          emergencyContactNumber: user.emergencyContactNumber,
          race: user.race,
          grade: eventUser.grade,
          schoolId: schoolUser ? parseInt(schoolUser.fetchSchool.id, 10) : undefined,
        });
      },
    },
  );
  const order = data?.fetchOrderDetail;
  const eventsUser = order?.fetchEventsUser;
  const event = eventsUser?.fetchEvent;
  const option = eventsUser?.fetchRegistrationOption;
  const schoolUser = eventsUser?.fetchSchoolUser;

  const checkPermissionOfEventsUser = useRegistrationOptionPermissionChecker(option);

  const [updateUser, { error, loading }] = useMutation(
    UPDATE_EVENT_USER_INFO,
    {
      onCompleted: () => {
        let path;
        if (checkPermissionOfEventsUser(Permissions.CAN_JOIN_COMPETITION)) {
          path = `/order/${orderId}/competitions`;
        } else if (event?.merchandise?.length > 0) {
          path = `/order/${orderId}/merch`;
        } else {
          path = `/order/${orderId}/cart`;
        }
        history.push(path);
      },
    },
  );

  useHeaderComponentsMutation({
    title: 'PERSONAL DETAILS',
    components: [{ key: EVENT_TITLE, value: get('title', event) }],
  });

  const handleChangeEmergencyContactNumber = (name, newValue) => {
    if (newValue === currentUser.phone) {
      setErrorMessage(emergencyContactError);
    } else {
      setErrorMessage('');
    }
    onChange(name, newValue);
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (params.emergencyContactNumber === currentUser.phone) {
      const element = document.getElementById('emergencyContactNumber');
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    if (params.schoolId !== parseInt(schoolUser.fetchSchool.id, 10) && schoolIdFile == null) {
      setErrorMessage('Please upload a school verification document.');
      return;
    }

    updateUser({
      variables: {
        input: params,
        image: schoolIdFile,
      },
    });
  };

  if (error) {
    return <GenericAlert>{extractError(error)}</GenericAlert>;
  }

  if (loading || orderLoading) {
    return <Loader />;
  }

  return (
    <>
      <div className="row">
        <div className="col-12 mx-auto px-4 pt-3">
          <JoinEventSlider
            event={event}
            order={order}
            selectedRegistrationOption={option}
          />
          <p style={{ fontWeight: '600' }}>Please Confirm the Following Details</p>
          <Form onSubmit={onSubmit}>
            <div style={{ margin: 'auto', textAlign: 'center' }} />
            {errorMessage ? <GenericAlert>{errorMessage}</GenericAlert> : null}
            <Input
              type="text"
              title="Emergency Contact Name"
              id="emergencyContactName"
              name="emergencyContactName"
              placeholder="Emergency Contact Name"
              required={isUnder18}
              onChange={onChange}
              withFormGroup
              label="Emergency Contact Name"
              labelClass="sr-only"
              params={params}
            />
            <Input
              title="Emergency Contact Phone Number"
              id="emergencyContactNumber"
              name="emergencyContactNumber"
              placeholder="Emergency Contact Phone Number"
              required={isUnder18}
              onChange={handleChangeEmergencyContactNumber}
              withFormGroup
              label="Emergency Contact Phone Number"
              labelClass="sr-only"
              params={params}
            />
            <Input
              title="Race/Ethnicity"
              name="race"
              type="select"
              params={params}
              id="race"
              required
              onChange={onChange}
              info="For demographics research purposes only"
            >
              <option value="" selected disabled>Select Race/Ethnicity</option>
              {Object.keys(raceChoices).map((key) => (
                <option value={key}>{raceChoices[key]}</option>
              ))}
            </Input>
            <div className="col-12 p-0">
              <Title>
                <p className="mt-4">
                  Confirm School
                  <span style={{ color: 'red' }}> *</span>
                </p>
              </Title>
              <div style={{ marginInline: '-15px' }}>
                <SchoolSearch
                  selectedSchool={selectedSchool}
                  onSelectSchool={handleSelectSchool}
                />
              </div>
              {schoolUser && selectedSchool.id === parseInt(schoolUser.fetchSchool.id, 10)
                ? (
                  <p className="pt-1">
                    <a href={schoolUser.fetchUserAttachment?.[0]?.verificationFileUrl} target="_blank" rel="noopener noreferrer">Click here</a>
                    {' '}
                    to view your existing school verification document.
                  </p>
                ) : null}
              {selectedSchool.id
                ? (
                  <>
                    <Title>
                      <p className="mt-4">
                        Upload School Verification
                        <span style={{ color: 'red' }}> *</span>
                      </p>
                    </Title>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        setSchoolIdFile(e.target.files[0]);
                      }}
                    />
                  </>
                )
                : null}
              <div className="pt-3" />
            </div>
            <Input
              title="Grade Level"
              name="grade"
              type="select"
              params={params}
              id="grade"
              required
              onChange={onChange}
            >
              <option value="" disabled>Select Grade Level</option>
              <option value={7}>7</option>
              <option value={8}>8</option>
              <option value={9}>9</option>
              <option value={10}>10</option>
              <option value={11}>11</option>
              <option value={12}>12</option>
              <option value={13}>13 / Gap Year</option>
              <option value={0}>Other</option>
            </Input>
            <Button
              backgroundcolor="#F4AB37"
              fontColor="#FFF"
              border="2px solid #FFF"
              marginbottom="-2rem"
              addNewText="CONTINUE"
              permissionName="View Event"
              loading={loading}
              htmltype="submit"
            >
              CONTINUE
            </Button>
            <div className="pt-3" />
          </Form>
          <BackLink to={`/event-manage/${order.eventId}`} imageNotRequired>
            Skip For Now
          </BackLink>
        </div>
      </div>
    </>
  );
};

export default JoinEventPersonalDetails;
